import { useState } from 'react';

export default function ContactAndCTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d52q372bfdatfusc40t0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <>
      {/* CTA Banner */}
      <section className="relative py-20 lg:py-28 overflow-hidden bg-[#0A2818]">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b9f62902dd2ac83be028725f667bd0eb.jpg Vegas Nevada aerial night skyline view from luxury high-rise medical office glowing Strip city lights bokeh dramatic dark atmosphere deep emerald forest green tones premium concierge healthcare aesthetic Nevada desert night sky stars distant mountains opulent upscale dark cinematic mood&width=1440&height=600&seq=cta-vegas-mc&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-center opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A2818]/90 to-[#0A2818]/60"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-xs font-semibold tracking-widest uppercase text-teal-400 mb-5">
                Begin Your Journey
              </span>
              <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Care Worth<br />
                <em className="not-italic text-teal-400">Signing For.</em>
              </h2>
              <p className="text-white/60 text-lg leading-relaxed mb-8">
                Join Nevada&apos;s most distinctive medical membership and experience healthcare redefined.
              </p>
              <div className="flex flex-wrap gap-6">
                {[
                  { icon: 'ri-shield-check-line', text: 'Nevada Licensed' },
                  { icon: 'ri-award-line', text: 'HIPAA Compliant' },
                  { icon: 'ri-user-star-line', text: 'Named Provider' },
                ].map((b) => (
                  <div key={b.text} className="flex items-center gap-2 text-white/70 text-sm">
                    <i className={`${b.icon} text-teal-400`}></i>
                    {b.text}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row lg:flex-col gap-4 lg:items-end">
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-teal-500 hover:bg-teal-400 text-white font-bold rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer text-base"
              >
                Schedule Consultation
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="tel:702-723-0303"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-white/10 hover:bg-white/15 border border-white/20 text-white font-semibold rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer text-base backdrop-blur-sm"
              >
                <i className="ri-phone-line text-teal-400"></i>
                702.723.0303
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-24 lg:py-32 bg-[#F8FAFB]">
        <div className="max-w-6xl mx-auto px-6 lg:px-16">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Left side info */}
            <div className="lg:col-span-2">
              <span className="inline-block text-xs font-semibold tracking-widest uppercase text-teal-600 mb-4">
                Get Started
              </span>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-bold text-[#0F1C2E] mb-5 leading-tight">
                Begin Your Signature Experience
              </h2>
              <p className="text-gray-500 text-base leading-relaxed mb-10">
                Schedule a consultation to learn more about Signature membership and how we can elevate your healthcare experience.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-xl flex-shrink-0">
                    <i className="ri-phone-line text-teal-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-1">Phone</p>
                    <a href="tel:702-723-0303" className="text-[#0F1C2E] font-semibold hover:text-teal-600 transition-colors cursor-pointer text-sm">
                      702.723.0303
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-xl flex-shrink-0">
                    <i className="ri-mail-line text-teal-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-1">Email</p>
                    <a href="mailto:signature@med-careproviders.com" className="text-[#0F1C2E] font-semibold hover:text-teal-600 transition-colors cursor-pointer text-sm break-all">
                      signature@med-careproviders.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-xl flex-shrink-0">
                    <i className="ri-map-pin-line text-teal-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-1">Location</p>
                    <p className="text-[#0F1C2E] font-semibold text-sm">
                      2121 E Flamingo Rd Ste 218<br />
                      Las Vegas, NV 89119
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-xl flex-shrink-0">
                    <i className="ri-time-line text-teal-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-1">Hours</p>
                    <p className="text-[#0F1C2E] font-semibold text-sm">
                      Mon–Fri: 8AM–5PM<br />
                      Sat: 9AM–2PM
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-gray-100 p-8 lg:p-10">
                <form id="signature-contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-xs font-semibold text-[#0F1C2E] mb-2 uppercase tracking-wide">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors text-sm bg-[#F8FAFB]"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs font-semibold text-[#0F1C2E] mb-2 uppercase tracking-wide">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors text-sm bg-[#F8FAFB]"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-xs font-semibold text-[#0F1C2E] mb-2 uppercase tracking-wide">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="(702) 555-0123"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors text-sm bg-[#F8FAFB]"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-xs font-semibold text-[#0F1C2E] mb-2 uppercase tracking-wide">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      maxLength={500}
                      rows={4}
                      placeholder="Tell us about your healthcare needs and which plan interests you..."
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors resize-none text-sm bg-[#F8FAFB]"
                    ></textarea>
                    <p className="text-xs text-gray-400 mt-1.5">{formData.message.length}/500 characters</p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-bold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Consultation Request
                        <i className="ri-arrow-right-line"></i>
                      </>
                    )}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="p-4 bg-teal-50 border border-teal-200 rounded-xl flex items-center gap-3">
                      <i className="ri-checkbox-circle-line text-teal-600 text-xl"></i>
                      <p className="text-teal-800 font-semibold text-sm">
                        Thank you! We&apos;ll contact you within 24 hours to schedule your consultation.
                      </p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                      <i className="ri-error-warning-line text-red-600 text-xl"></i>
                      <p className="text-red-800 font-semibold text-sm">
                        Something went wrong. Please try again or call us directly.
                      </p>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
